import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Layout, Button, Paper } from "$components";
import useJStyles from "$hooks/useJStyles";
import {
  getCompanyTypeLabel,
  companyTypesOptions,
} from "$utils/typeConstants/Company";
import {
  DataTableWithContext,
  EnumDataRowType,
  EnumFilterTypes,
} from "$components/DataTable";
import { getCompanies } from "$services/Company.service";
import { useQueryParameters } from "$hooks/useQueryParameters";
import { usePopin } from "$components/Popin/usePopin";
import EntrepriseForm from "./EntrepriseForm";
import useCompanyForm from "$hooks/useCompanyForm";
import { Switch } from "$components/Forms/Switch";
import { CacheInvalidationAction } from "$utils/cache";
import { appState } from "$reducers";

const useMakeCell = (value) => {
  const navigate = useNavigate();
  const link = ({ _id }) => `/entreprise/${_id}`;

  return {
    type: EnumDataRowType.Link,
    link,
    value,
    onClick: (row) => {
      navigate(link(row));
    },
  };
};

const CompaniesContainer = () => {
  const [isActive, setisActive] = useState(true);
  const { jclasses } = useJStyles();
  const { Popin, PopinProps, togglePopin } = usePopin();
  const params = useQueryParameters();
  const { filter: companySidebarFilter } = useSelector(appState);
  const navigate = useNavigate();

  useEffect(() => {
    if (companySidebarFilter && navigate) {
      navigate(`/entreprise/${companySidebarFilter?._id}`);
    }
  }, [companySidebarFilter, navigate]);

  // @todo amir: why it's useless ? we still need it to make the component reusable !
  // TODO will remove this with the form update, it's useless
  const { handleSubmit, error, submitting } = useCompanyForm(
    undefined,
    togglePopin
  );

  const headerData = [
    {
      id: "name",
      filter: {
        type: EnumFilterTypes.INPUT,
        label: "Entreprise",
        placeholder: "Par nom",
        value: params.get("name"),
      },
      minWidth: 170,
      cell: { ...useMakeCell(({ name }) => name), isDisabled: !isActive },
    },
    {
      id: "companyTypes",
      filter: {
        type: EnumFilterTypes.SELECT,
        value: ({ companyTypes }) => getCompanyTypeLabel(companyTypes),
        styleFilter: { maxWidth: 250 },
        options: companyTypesOptions,
        label: "Type",
      },
      minWidth: 170,
      cell: {
        ...useMakeCell(({ companyTypes }) =>
          companyTypes.map((ct) => getCompanyTypeLabel(ct)).join(" - ")
        ),
        isDisabled: !isActive,
      },
    },
  ];

  const handleSwitch = () => {
    setisActive(!isActive);
  };

  const externalState = useMemo(() => {
    const returnState = {};

    for (const [key, value] of params.entries()) {
      returnState[key] = value;
    }
    const { page, rowsPerPage, ...filters } = returnState as any;

    return {
      filters: { ...filters, isActive },
      pagination: { page, rowsPerPage },
    };
  }, [isActive, params]);

  return (
    <Box className={jclasses.jWrapper}>
      <Layout
        title="Liste des entreprises"
        contentClassName={jclasses.jLayoutCustom}
        isHeaderRight
      >
        <Paper className={jclasses.jPaper}>
          <DataTableWithContext
            externalState={externalState}
            filterList={[
              <Switch
                key="active-companies"
                label="Entreprises actives"
                onChange={handleSwitch}
                checked={isActive}
              />,
            ]}
            buttonList={[
              <Button
                key="add-company"
                variant="outlined"
                onClick={togglePopin}
                startIcon={<AddIcon />}
              >
                Ajouter une entreprise
              </Button>,
            ]}
            rowsPerPage={50}
            thead={headerData}
            queryFunction={getCompanies}
            queryFunctionName={CacheInvalidationAction.GET_COMPANIES}
            data-testid="companyList"
          />
        </Paper>
        <Popin {...PopinProps}>
          <EntrepriseForm
            onSubmit={handleSubmit}
            onReset={togglePopin}
            companyError={error}
            isSubmitting={submitting}
          />
        </Popin>
      </Layout>
    </Box>
  );
};

export default CompaniesContainer;
